export const LEGAL_PAGES = [
    {
        slug: "privacy-policy",
        title: "Privacy Policy",
        eyebrow: "Legal",
        updated: "Last updated: June 2026",
        intro:
            "This Privacy Policy explains how Pocketfolio Ltd ('Pocketfolio', 'we', 'us') collects, uses and protects personal information when you visit pocketfolio.ai or contact our desk.",
        sections: [
            {
                h: "1. Who we are",
                ps: [
                    "Pocketfolio Ltd is a company registered in the Union of the Comoros, with its registered office at Bonovo Road – Fomboni, Island of Mohéli, Comoros Union. For any privacy-related enquiry, contact us at contact@pocketfolio.ai.",
                ],
            },
            {
                h: "2. Information we collect",
                ps: [
                    "Enquiry data: when you submit our contact form we collect your name, email address, the service you are interested in, and the content of your message.",
                    "Technical data: our hosting infrastructure may record standard server logs, including IP address, browser type and pages visited, used solely for security and operational purposes.",
                    "Third-party widgets: live market data on this site is rendered by embedded TradingView widgets. TradingView may set its own cookies and collect usage data under its own privacy policy. We do not control or receive that data.",
                ],
            },
            {
                h: "3. How we use your information",
                ps: [
                    "We use enquiry data to respond to your request, assess suitability for our services, and follow up on your enquiry. We do not sell, rent or trade personal information to third parties.",
                    "We may use your email address to send information you have specifically requested. We do not operate automated marketing lists unless you have explicitly opted in.",
                ],
            },
            {
                h: "4. Legal basis and retention",
                ps: [
                    "We process enquiry data on the basis of your consent, given when you submit the contact form, and our legitimate interest in responding to prospective clients.",
                    "Enquiry records are retained for as long as reasonably necessary to handle your request and comply with our record-keeping obligations, after which they are deleted.",
                ],
            },
            {
                h: "5. Data storage and security",
                ps: [
                    "Enquiry data is stored in a secured database with access restricted to authorised personnel. We apply reasonable technical and organisational measures to protect personal data against unauthorised access, alteration or loss.",
                    "No method of transmission over the internet is completely secure; you submit information at your own discretion.",
                ],
            },
            {
                h: "6. Your rights",
                ps: [
                    "You may request access to, correction of, or deletion of the personal information we hold about you at any time by writing to contact@pocketfolio.ai. We will respond within a reasonable period.",
                ],
            },
            {
                h: "7. Cookies",
                ps: [
                    "Pocketfolio does not set first-party tracking cookies. Embedded third-party services (such as TradingView) may set cookies required for their widgets to function. You can control cookies through your browser settings.",
                ],
            },
            {
                h: "8. Changes to this policy",
                ps: [
                    "We may update this policy from time to time. The 'Last updated' date at the top of this page indicates the most recent revision. Continued use of the site after changes constitutes acceptance of the revised policy.",
                ],
            },
        ],
    },
    {
        slug: "terms",
        title: "Terms & Conditions",
        eyebrow: "Legal",
        updated: "Last updated: June 2026",
        intro:
            "These Terms & Conditions govern your use of pocketfolio.ai and any engagement with the products and services offered by Pocketfolio Ltd. By using this website, you agree to these terms.",
        sections: [
            {
                h: "1. The company",
                ps: [
                    "Pocketfolio Ltd is registered in the Union of the Comoros at Bonovo Road – Fomboni, Island of Mohéli, Comoros Union. References to 'we', 'us' or 'the desk' mean Pocketfolio Ltd.",
                ],
            },
            {
                h: "2. Nature of our services",
                ps: [
                    "Pocketfolio provides algorithmic trading software ('bots'), trade signal publications and trading education focused on commodities, stocks and ETFs.",
                    "Pocketfolio is not a broker, investment adviser, portfolio manager or fiduciary. Nothing on this website constitutes investment advice, a personal recommendation, or an offer or solicitation to buy or sell any financial instrument.",
                    "All purchases are concluded through a direct enquiry with our desk. Displayed prices are indicative and subject to confirmation, availability and suitability assessment before onboarding.",
                ],
            },
            {
                h: "3. Eligibility",
                ps: [
                    "Our services are intended for persons aged 18 or over. It is your responsibility to ensure that using our products and trading leveraged instruments is lawful in your jurisdiction. We may refuse service at our discretion.",
                ],
            },
            {
                h: "4. Licences and acceptable use",
                ps: [
                    "Bot licences are personal, non-exclusive and non-transferable, limited to the number of trading accounts specified in your plan. You may not resell, redistribute, decompile or reverse-engineer our software.",
                    "Signal content is provided for your personal use only and may not be republished, resold or redistributed in any form.",
                ],
            },
            {
                h: "5. Performance information",
                ps: [
                    "Any performance figures shown on this website are derived from backtested and forward-tested records on simulated accounts, are self-reported and unaudited, and are provided for illustration only. Past performance is not indicative of future results. Live results will differ due to broker execution, spreads, slippage and market conditions.",
                ],
            },
            {
                h: "6. Fees and refunds",
                ps: [
                    "Fees, billing cycles and any applicable refund terms are agreed in writing with the desk before onboarding. Unless expressly agreed otherwise, fees for delivered software licences, published signals and commenced education programmes are non-refundable.",
                ],
            },
            {
                h: "7. Limitation of liability",
                ps: [
                    "To the maximum extent permitted by law, Pocketfolio Ltd shall not be liable for any trading losses, loss of profit, loss of data or indirect or consequential damages arising from the use of this website, our software, signals or educational content.",
                    "You retain full responsibility for every trading decision made on your accounts, whether manual or automated.",
                ],
            },
            {
                h: "8. Intellectual property",
                ps: [
                    "All content on this website — including software, text, branding, and educational material — is the property of Pocketfolio Ltd or its licensors and is protected by applicable intellectual property laws.",
                ],
            },
            {
                h: "9. Governing law",
                ps: [
                    "These terms are governed by the laws of the Union of the Comoros. Any dispute shall be subject to the exclusive jurisdiction of the courts of the Union of the Comoros.",
                ],
            },
            {
                h: "10. Changes",
                ps: [
                    "We may revise these terms at any time. Continued use of the website after publication of revised terms constitutes acceptance.",
                ],
            },
        ],
    },
    {
        slug: "risk-disclosure",
        title: "Risk Disclosure",
        eyebrow: "Legal",
        updated: "Last updated: June 2026",
        intro:
            "Trading leveraged financial instruments involves significant risk. Read this disclosure carefully before using any Pocketfolio product or acting on any information published on this website.",
        sections: [
            {
                h: "1. General risk warning",
                ps: [
                    "Trading foreign exchange, commodities, stock indices, stocks and ETFs — particularly on margin — carries a high level of risk and may not be suitable for all investors. You could lose some or all of your invested capital. Never trade with money you cannot afford to lose.",
                ],
            },
            {
                h: "2. No investment advice",
                ps: [
                    "Pocketfolio publishes software, signals and education for informational and educational purposes only. Nothing we publish takes into account your personal financial situation, objectives or risk tolerance. If in doubt, seek advice from an independent, licensed financial adviser.",
                ],
            },
            {
                h: "3. Automated trading risk",
                ps: [
                    "Algorithmic trading systems can fail. Connectivity outages, broker execution issues, extreme volatility, gaps and black-swan events can cause a bot to behave differently from its tested behaviour and can produce losses larger than historical drawdowns suggest.",
                    "You are responsible for supervising any automated system connected to your account, including risk settings, VPS uptime and broker conditions.",
                ],
            },
            {
                h: "4. Signal risk",
                ps: [
                    "Trade signals reflect the opinion of our research desk at the time of publication. Markets move quickly; entries, stops and targets may become invalid before you act on them. Execution of any signal is entirely your decision and responsibility.",
                ],
            },
            {
                h: "5. Performance disclaimer",
                ps: [
                    "All performance figures on this website are simulated or self-reported, unaudited, and shown net of assumed costs. Hypothetical performance has inherent limitations: it does not reflect real capital at risk and is often prepared with the benefit of hindsight. Past performance — real or simulated — is not indicative of future results.",
                ],
            },
            {
                h: "6. Leverage",
                ps: [
                    "Leverage magnifies both gains and losses. A small adverse market movement can result in losses exceeding your initial deposit with some brokers. Understand your broker's margin policy before trading.",
                ],
            },
            {
                h: "7. Jurisdiction",
                ps: [
                    "Products and services described on this website may not be available or lawful in all jurisdictions. It is your responsibility to comply with the laws applicable to you before purchasing or using any Pocketfolio product.",
                ],
            },
        ],
    },
];
